import React, { useEffect, useRef, useState } from "react";
import { PlIcon } from "../list/plIcons";
import { FieldSpec, isMarkOn } from "./viewProductFields";

type Props = {
  spec: FieldSpec;
  /** Text shown at rest; "" offers "+ Add". */
  value: string;
  /** Text the editor starts from (tags joined with ", ", marks "true"/"false"). */
  raw: string;
  changed?: boolean;
  liveValue?: string;
  /** Saves this one field. Reject with an Error whose message the row shows; the typed text stays. */
  onSave: (text: string) => Promise<void>;
  /** Picker rows (category, type tags): the pencil opens a sheet (phone) or a popover by the row (wide) instead of editing inline. */
  onOpen?: (anchor: DOMRect) => void;
  disabled?: boolean;
};

/**
 * One row of the in-place editor (Figma V01–V04, V09): label, value, pencil. The pencil turns the row
 * into an input with Cancel / Save; Save writes only this field. A failed save keeps the text and offers Retry.
 */
const FieldRow = ({ spec, value, raw, changed = false, liveValue, onSave, onOpen, disabled = false }: Props) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(raw);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setText(raw);
  }, [raw, editing]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const start = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    if (onOpen) {
      onOpen(event.currentTarget.getBoundingClientRect());
      return;
    }
    setError(null);
    setText(raw);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setError(null);
    setText(raw);
  };
  const save = async (next: string) => {
    setBusy(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e: any) {
      setError(e?.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  if (spec.kind === "mark") {
    const on = isMarkOn(raw);
    return (
      <div className="vp-row is-mark">
        <div className="vp-row__main">
          <div className="vp-row__label">
            {spec.label}
            {changed ? <span className="vp-chg">changed</span> : null}
          </div>
          {error ? <div className="vp-row__error">{error}</div> : null}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={spec.label}
          className={`vp-switch${on ? " is-on" : ""}`}
          disabled={busy || disabled}
          onClick={() => void save(on ? "false" : "true")}
        />
      </div>
    );
  }

  if (editing) {
    const multi = spec.kind === "textarea" || spec.kind === "tags";
    const onKey = (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        cancel();
      } else if (event.key === "Enter" && !multi) {
        event.preventDefault();
        void save(text);
      }
    };
    return (
      <div className="vp-row is-editing">
        <div className="vp-row__label">
          <span>{spec.label}</span>
          {spec.maxLength ? (
            <i>
              {text.length} / {spec.maxLength}
            </i>
          ) : null}
        </div>
        {multi ? (
          <textarea
            ref={(el) => {
              inputRef.current = el;
            }}
            className="vp-input"
            value={text}
            rows={spec.kind === "tags" ? 2 : 4}
            maxLength={spec.maxLength}
            placeholder={spec.placeholder}
            aria-label={spec.label}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={busy}
          />
        ) : (
          <input
            ref={(el) => {
              inputRef.current = el;
            }}
            className="vp-input"
            type="text"
            value={text}
            maxLength={spec.maxLength}
            inputMode={spec.inputMode}
            placeholder={spec.placeholder}
            aria-label={spec.label}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            disabled={busy}
            enterKeyHint="done"
          />
        )}
        {spec.kind === "tags" ? <div className="vp-row__hint">Separate tags with commas</div> : null}
        {error ? (
          <div className="pl-alert is-warn vp-row__alert" role="alert">
            <PlIcon name="warn" size={18} />
            <span>
              <b>{error}</b> Your text is kept; nothing else on the piece changed.
            </span>
          </div>
        ) : null}
        <div className="vp-row__actions">
          <span className="vp-row__hint">Saves only this field</span>
          <button type="button" className="pl-btn is-sm" onClick={cancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="pl-btn is-primary is-sm" onClick={() => void save(text)} disabled={busy}>
            {busy ? "Saving…" : error ? "Retry" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  const empty = value === "";
  return (
    <div className={`vp-row${changed ? " is-changed" : ""}`}>
      <button type="button" className="vp-row__main" onClick={start} disabled={disabled} aria-label={`${empty ? "Add" : "Edit"} ${spec.label}`}>
        <div className="vp-row__label">
          {spec.label}
          {changed ? <span className="vp-chg">changed</span> : null}
        </div>
        {empty ? <div className="vp-row__value is-empty">+ Add</div> : <div className={`vp-row__value${spec.kind === "textarea" ? " is-clamp" : ""}`}>{value}</div>}
        {changed && liveValue ? <div className="vp-row__live">Live: {liveValue}</div> : null}
      </button>
      {disabled ? null : (
        <button type="button" className="vp-pen" aria-hidden="true" tabIndex={-1} onClick={start}>
          <PlIcon name={onOpen ? "right" : "edit"} size={16} />
        </button>
      )}
    </div>
  );
};

export default FieldRow;
